import { Helper } from "../helpers/helper";
import { PopupCloseCallback } from "./popup.models";

declare global {
    interface Window {
        FindURLParameter: (key: string) => string;
        Page_Validators: HTMLElement[];
        ValidatorValidate: (validator: HTMLElement) => void;
        ValidatorOnChange_Original: (target: any) => void;
        ValidatorOnLoad: () => void;
        ValidatorEnable: (val: HTMLElement, enable: boolean) => void;
        Page_ClientValidate: (validationGroup?: string) => boolean;
        GetRadWindow: () => Telerik.Web.UI.RadWindow;
        GetRadWindowManager: () => Telerik.Web.UI.RadWindowManager;
        ShowDialog: (url: string, args: any, width: string, height: string, title: string, iconUrl: string, templateType: string, callBackFunction: () => void, windowName: string, closeWindowOnCommit: boolean, preserveStatefulBusinessContainer: boolean, dialogCloseFunction: PopupCloseCallback) => void;
        showStatusMessage: (messageContent: string, messageType: "Information" | "Success" | "Warning" | "Error" | "Validation" | "Neutral") => void;
        jQuery: JQueryStatic;
    }

    interface HTMLElement<T = any> {
        validationGroup: string;
        control: T;
    }

    /**
    * Returns the specified Component object. This member is static and can be invoked without creating an instance of the class.
    * @see {@link http://msdn.microsoft.com/en-us/library/bb397441(v=vs.100).aspx}
    * @param id A string that contains the ID of the component to find.
    * @param parent (Optional) The component or element that contains the component to find.
    * @return A Component object that contains the component requested by ID, if found; otherwise, null.
    */
    function $find<T>(id: string, parent?: Sys.Component): T;
    function $find<T>(id: string, parent?: HTMLElement): T;
}

/** iMIS status message types, mapped to the CSS/message class names used by ASI's status message framework. */
export enum ImisMessageType {
    SUCCESS = "AsiSuccess",
    INFORMATION = "AsiInformation",
    WARNING = "AsiWarning",
    ERROR = "AsiError"
}

/** Maps a payload field name to its expected iMIS payload field type. */
export type PayloadFieldToTypeMapping = {
    [key: string]: ImisPayloadFieldType
};

/** Data types recognized when coercing/serializing values for an iMIS payload field. */
export enum ImisPayloadFieldType {
    BINARY = "binary",
    STRING = "string",
    BOOLEAN = "boolean",
    NUMBER = "number",
    BIGINT = "bigint",
}

/** Query parameters for an iMIS REST list request: a partial match against `T`'s fields plus any additional REST query parameters (e.g. `limit`, `offset`). */
export type ImisRestRequestFilter<T> = Partial<T> & Record<string, any>;

/** Client-side context describing the current iMIS site/session (base URLs, party identity, and cookie consent). */
export class ImisClientContext {
    appTimeZoneOffset: number;
    baseUrl: string;
    cookieConsent: number;
    isAnonymous: boolean;
    loggedInPartyId: string;
    selectedPartyId: string;
    virtualDir: string;
    websiteRoot: string;
}

/** The OAuth grant type used when authenticating against the iMIS REST API. */
export type ImisAuthenticationGrantType = "password" | "refresh_token";

/** Credentials payload used to request an iMIS REST API access token via username/password or a refresh token. */
export class ImisAuthentication {
    username: string;
    password: string;
    grant_type: ImisAuthenticationGrantType;
    refresh_token?: string;

    /**
     * Creates an ImisAuthentication payload, selecting the "refresh_token" grant type when a refresh token is supplied, or "password" otherwise.
     * @param username The iMIS account username.
     * @param password The iMIS account password.
     * @param refreshToken Optional refresh token; when provided, grant_type is set to "refresh_token" instead of "password".
     */
    constructor(username: string, password: string, refreshToken?: string) {
        this.username = username;
        this.password = password;
        this.refresh_token = refreshToken;
        this.grant_type = Helper.isStringNullOrEmpty(this.refresh_token) ? "password" : "refresh_token";
    }
}

/** Raw credentials (site URL plus username/password or refresh token) used to obtain an iMIS access context. */
export type ImisCredentials = {
    url: string;
    username: string;
    password: string;
    refresh_token?: string;
};

/** The access token response returned by the iMIS OAuth token endpoint. */
export type ImisAccessContext = {
    ".expires": string;
    ".issued": string;
    TenantId: string;
    access_token: string;
    "as:client_id": string;
    expires_in: number;
    refresh_token: string;
    token_type: string;
    userName: string;
};

/** Generic wrapper for an iMIS REST API response whose payload lives under a dynamic `Properties` bag. */
export class ImisResponse {
    Properties: any = {};
}

/** Complex type wrapper commonly returned for binary fields, pairing the .NET type name with the field's value (base64-encoded for binary data). */
export type ImisComplexType = {
    /** The .NET type name of the value (e.g. `System.Byte[]`). */
    $type: string;
    /** The value itself; base64-encoded for binary fields. */
    $value: string;
};

/** A paged collection response from the iMIS REST API, with items already unwrapped into a plain array. */
export class ImisResponseItems<T = ImisResponse> {
    Count: string;
    HasNext: boolean;
    Limit: number;
    NextOffset: number;
    NextPageLink: string;
    Offset: number;
    TotalCount: number;
    Items: T[];
}

/** A paged collection response from the iMIS REST API in its raw wire format, where items are nested under an `$values` array. */
export class ImisResponseItemsRaw<T = any> {
    Count: string;
    HasNext: boolean;
    Limit: number;
    NextOffset: number;
    NextPageLink: string;
    Offset: number;
    TotalCount: number;
    Items: { $values: T[] };
}

/** Generic envelope for an iMIS REST API result, including HTTP status/caching metadata and validation errors/warnings. */
export class ImisResponseResult<T> {
    IsCachedResult: boolean;
    IsSuccessStatusCode: boolean;
    Message: string;
    ReasonPhrase: string;
    Result: T;
    StatusCode: number;
    ValidationResults: {
        Errors: any[];
        Warnings: any[];
    }
}

/** Response payload returned to an iMIS form webhook caller, indicating success/failure and an accompanying message. */
export class ImisFormWebhookResponse {
    result: boolean;
    message: string;

    /**
     * Creates an ImisFormWebhookResponse defaulting to a successful result with an empty message.
     */
    constructor() {
        this.result = true;
        this.message = "";
    }
}

/** The cookie and header values that together make up an ASP.NET request verification (anti-forgery) token pair. */
export type ImisRequestVerificationToken = {
    cookie: string;
    header: string;
}

/** Metadata describing an iMIS business object panel's source entity, including its properties and index definitions. */
export class PanelSourceMetadata {
    Description: string;
    EntityTypeName: string;
    Indexes: any[];
    PrimaryParentEntityTypeName: string;
    Properties: PanelSourceMetadataProperty[];
}

/** Metadata describing a single property of an iMIS panel source entity, including display, type, and validation attributes. */
export class PanelSourceMetadataProperty {
    // GENERAL
    Name: string;
    Description: string;
    PropertyTypeName: string;
    Caption: string;
    DefaultValue: string & { $value: any };
    DisplayMask: string;
    RenderingInformation: any;
    IsReadOnly: boolean;
    Rule: any;
    Visible: boolean;
    // STRING
    MaxLength: number;
    // DATETIME
    DateTimeDataType: number;
    // DECIMAL
    Scale: number;
    Precision: number;
}

/** Metadata describing an iMIS business object (BO) entity definition, including its type name and parent hierarchy. */
export class BOEntityDefinition {
    EntityTypeName: string;
    ObjectTypeName: string;
    PrimaryParentEntityTypeName: string;
    Description: string;
}

/** A single code/description entry from an iMIS generic (lookup) table. */
export class GenTableItem {
    Table_Name: string;
    Code: string;
    Description: string;
}

/** An iMIS panel definition, describing its layout of groups, columns, and fields for rendering/editing a business object. */
export class PanelDefinitionItem {
    PanelDefinitionId: string;
    Name: string;
    IsMultipleInstance: boolean;
    RelatedEntityName: string;
    SelectedEntityNames: string[];
    Description: string;
    Caption: string;
    Groups: {
        Columns: {
            Fields: {
                Caption: string;
                EntityName: string;
                HideInNormalView: boolean;
                IsReadOnly: boolean;
                IsRequired: boolean;
                LabelPosition: number;
                NumberOfLinesToDisplay: number;
                PropertyName: string;
            }[];
        }[];
    }[];
}

/** A single entry from an iMIS object's change log, describing the action taken and the property changes involved. */
export class ChangeLogItem {
    // TODO: ActionType should be an enum for clarity
    ActionType: number;
    ChangeDate: string;
    ChangedByUserId: string;
    Changes: ChangeLogChange[];
}

/** A single property-level change recorded within a change log entry, capturing its old and new values. */
export class ChangeLogChange {
    NewValue: string;
    Ordinal: number;
    OriginalValue: string;
    PropertyName: string;
}

/** An iMIS content item, describing a rendered content part's display, layout, and styling settings. */
export class ContentItem {
    Data: {
        Collapsed: boolean,
        Collapsible: boolean,
        ContentItemKey: string,
        ContentItemName: string,
        ContentKey: string,
        ContentTypeKey: string,
        CssClass: string,
        DisplayOnExtraSmallScreens: boolean,
        DisplayOnLargeScreens: boolean,
        DisplayOnMediumScreens: boolean,
        DisplayOnSmallScreens: boolean,
        DoNotRenderInDesignMode: boolean,
        IconUrl: string,
        LayoutZone: number,
        LicenseKeyRestriction: boolean,
        ModuleSpecificSetting: boolean,
        PartTitle: string,
        PartTitleHeadingLevel: number,
        Settings: { [key: string]: any },
        ShowBorder: boolean,
        ShowTitleFlag: boolean,
        SortOrder: number
    }
}

/** An iMIS document library item, describing a stored document's identity, type, versioning, and status. */
export class DocumentItem {
    /** This is the document's non-coded display name. It isn't the document's name used within the URL. */
    AlternateName: string;
    Description: string;
    /** Identifies one specific version of the document. */
    DocumentId: string;
    /** The ID of the document's type (e.g. the type used to filter in `DocumentsUtility.getDocumentsByType`). */
    DocumentTypeId: string;
    /** Identifies the document across all of its versions. */
    DocumentVersionId: string;
    /** This is the document's coded display name. It is the document's name used within the URL. */
    Name: string;
    /** The document's location in the document library. */
    Path: string;
    /** The document's publish status (e.g. "Published"). */
    Status: string;
    /** The document's content as a binary complex type; only populated when the document is retrieved from `/api/document` rather than the `/api/documentsummary` listing. */
    Data: ImisComplexType;
}

/** An iMIS query (Informer) definition, including its underlying document, sources, relations, and displayed properties. */
export class QueryDefinitionItem {
    Document: DocumentItem;
    Parameters: any[];
    Path: string;
    Properties: {
        Alias: string;
        Caption: string;
        DataTypeName: string;
        DisplayFormat: string;
        DisplayOrder: number;
        Link: string;
        Name: string;
        PropertyName: string;
        QuerySourceId: string;
    }[];
    QueryDefinitionId: string;
    Relations: {
        LeftPropertyName: string;
        LeftQuerySourceId: string;
        RelationType: string;
        RightPropertyName: string;
        RightQuerySourceId: string;
    }[];
    Sources: {
        BusinessControllerName: string;
        Description: string;
        Name: string;
        QuerySourceId: string;
        QuerySourceType: number;
    }[];
}

/** An iMIS Form Designer form, including its HTML markup, versioning, status, and business rules script. */
export class FormItem {
    FormDesignerLibraryId: number;
    FormName: string;
    FormDescription: string;
    FormHtmlCode: string;
    FormStatus: string;
    FormVersion: number;
    LastSubmission: string;
    FormMessages: string;
    LastHealthCheck: string;
    FormRules: string;
    FormRulesScript: string;
    FormDesignerTypeCode: string;
    FormTypeCode: number;
    FormVersionKey: string;
}

/** An iMIS Form Designer form group/type, used to categorize forms in the FormDesignerLibrary. */
export type FormGroupItem = {
    FormDesignerTypeCode: string;
    FormDesignerTypeName: string;
}

/** A file attachment for a file field. Derived from an XML object. */
export type FileFieldAttachment = {
    UploadedFile: {
        FileContentType: string;
        FileType: string;
        UploadedFileName: string;
        File: string;
    }
};
